import WorkflowType from "../enums/workflowType";
import { Workflow } from "../types/starAPIResponseTypes"
import { Dict } from "../types/types";
import { getWorkflowGroups } from "./groupService";
import { genericRequest, workflowsRoute, workflowGroupRoute } from "./service"

/**
 * Uploads a workflow to the database.
 * 
 * @param workflow to upload
 * 
 * @returns created workflow
 */
export const addWorkflow = async (workflow: Workflow): Promise<Workflow | Error> => {
  const body = JSON.stringify(workflow)
  switch (workflow.workflow_type) {
    case WorkflowType.ANGULAR_SCOPE:
      return await genericRequest({ url: `${workflowsRoute}/angular-scope`, method: "POST", body: body })
    case WorkflowType.PATCH_SCOPE:
      return await genericRequest({ url: `${workflowsRoute}/patch-scope`, method: "POST", body: body })
  }
  
  return await genericRequest({ url: `${workflowsRoute}/angular`, method: "POST", body: body })
}

/**
 * Gets list of all stored workflows.
 * 
 * @returns Gets all workflows
 */
export const getWorkflows = async (limit?: number, offset?: number, types?: number[]): Promise<Workflow[] | Error> => {
  const query = {
    limit: `${limit ?? 0}`,
    skip: `${offset ?? 100}`,
    workflow_types: types ?? [WorkflowType.ANGULAR, WorkflowType.PATCH]
  }
  return await genericRequest({ url: `${workflowsRoute}/`, method: "GET", query: query })
}

// export const getIncomingWorkflows = async (limit?: number, offset?: number): Promise<Workflow[] | Error> => {
//   const query = {
//     limit: `${limit ?? 0}`,
//     skip: `${offset ?? 0}`,
//   };
//   return await genericRequest({ url: `${workflowsRoute}/not-in-group`, method: "GET", query: query })
// }


/**
 * Gets list of all workflows not assigned to a group..
 * 
 * @returns Gets all unassigned workflows
 */
export const getIncomingWorkflows = async (limit?: number, offset?: number): Promise<Workflow[] | Error> => {
  const query = {
    limit: `${limit ?? 0}`,
    skip: `${offset ?? 0}`,
    workflow_types: [WorkflowType.ANGULAR,WorkflowType.PATCH]
  }

  const workflows = await genericRequest({ url: `${workflowsRoute}/`, method: "GET", query: query })
  if (workflows instanceof Error) {
    return workflows
  }
  const workflowGroups = await getWorkflowGroups(10000, 0)
  if (workflowGroups instanceof Error) {
    return workflowGroups
  }
  
  const workflowGroupWorkflowIds = workflowGroups.map((workflowGroup) => workflowGroup.workflow_id)
  return (workflows as Workflow[]).filter((workflow) => !workflowGroupWorkflowIds.includes(workflow.workflow_id))
}

/**
 * Gets a workflow with a specified id.
 * 
 * @param id of workflow to fetch
 * 
 * @returns workflow for a given id
 */
export const getWorkflow = async (id: number): Promise<Workflow | Error> => {
  return await genericRequest({ url: `${workflowsRoute}/${id}`, method: "GET" })
}

/**
 * Deletes a workflow from the database.
 * 
 * @param workflowId of workflow to delete
 * 
 * @returns null when workflow deleted
 */
export const deleteWorkflow = async (workflowId: number) => {
  return await genericRequest({ url: `${workflowsRoute}/${workflowId}`, method: "DELETE" })
}

/**
 * Gets all workflows for a given group.
 * 
 * @param groupId id of the group to fetch workflows from
 * 
 * @returns list of all workflows belonging to the specified group
 */
export const getWorkflowsForGroup = async (groupId: number): Promise<Workflow[] | Error> => {
  return await genericRequest({ url: `${workflowsRoute}/group/${groupId}`, method: "GET" })
}

/**
 * Deletes a workflow group.
 * 
 * @param workflowId of workflow to remove
 * @param groupId of group to remove workflow from
 * 
 * @returns null
 */
export const removeWorkflowFromGroup = async (workflowId: number, groupId: number) => {
  return await genericRequest({ url: `${workflowGroupRoute}/${workflowId}/${groupId}`, method: "DELETE" })
}

/**
 * Updates a workflow.
 * 
 * @param workflowId to update.
 * @param workflowDict updated workflow.
 * 
 * @returns updated workflow.
 */
export const updateWorkflow = async (workflowId: number, workflowDict: Dict) => {
  const body = JSON.stringify(workflowDict)
  return await genericRequest({ url: `${workflowsRoute}/${workflowId}`, method: "PUT", body: body })
}

export const getWorkflowsForIds = async (workflowIds: number[]) => {
  const body = JSON.stringify(
    {
      ids: workflowIds
    }
  )
  return await genericRequest({ url: `${workflowsRoute}/batch`, method: "POST", body: body })
}